import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useNavigate } from '@tanstack/react-router'

import { ArrowLeftIcon } from '@gto/icons-react/outline/arrow-left'
import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CreditCardIcon } from '@gto/icons-react/outline/credit-card'
import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import type { SelectionModel } from '@gto/ui/data-table'
import { EMPTY_SELECTION } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import { DataTable } from '@/components/data-table'
import { SelectField } from '@/components/form'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { selectedRows } from '@/utils/table-selection'

import { useCreateSettlement, useEligiblePayouts, useSettlementOverview } from './api'
import { CREATABLE_PROVIDERS, PROVIDER_LABELS, providerEligibleCount } from './labels'
import { useEligiblePayoutColumns } from './use-eligible-payout-columns'

// ─── Steps ──────────────────────────────────────────────────────────────────

type Step = 'select-provider' | 'select-payouts' | 'confirm'

const STEP_LABELS: Record<Step, string> = {
  'select-provider': 'admin.settlements.createStep1',
  'select-payouts': 'admin.settlements.createStep2',
  confirm: 'admin.settlements.createStep3',
}

const STEP_NUMBER: Record<Step, number> = {
  'select-provider': 1,
  'select-payouts': 2,
  confirm: 3,
}

export const CreateSettlementPage = () => {
  const { formatMessage } = useIntl()
  // Top level, not inside the step-2 branch that renders the table: it is a hook.
  const payoutColumns = useEligiblePayoutColumns()
  const navigate = useNavigate()

  const [step, setStep] = useState<Step>('select-provider')
  const [provider, setProvider] = useState('')
  const [currency, setCurrency] = useState('USD')
  // The DS table's selection model is a discriminated record, not an id array.
  const [selection, setSelection] = useState<SelectionModel>(EMPTY_SELECTION)

  const overviewQuery = useSettlementOverview()
  const { data: overview, isError: overviewError, refetch: refetchOverview } = overviewQuery
  const payoutsQuery = useEligiblePayouts(provider)
  const {
    data: eligiblePayouts,
    isLoading: payoutsLoading,
    isError: payoutsError,
    refetch: refetchPayouts,
  } = payoutsQuery
  const createMutation = useCreateSettlement()

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(overviewQuery, 'useSettlementOverview failed: ')
  useReportQueryError(payoutsQuery, 'useEligiblePayouts failed: ')

  // Money-critical: a failed create must reach Sentry AND surface to the operator,
  // and must NOT navigate away as if the settlement was created.
  const createError = useMutationErrorHandler('useCreateSettlement failed: ')

  // Derivations — trivial filter/reduce, inlined per React 19 (React Compiler
  // handles memoisation). Amounts are coerced to numbers at the api.ts boundary,
  // so this reduce is real addition, not string concatenation.
  // Everything below reads from THIS, never the raw model: the total, the count on screen and
  // the ids that get POSTed — see `selectedRows` for why the table cannot be trusted to prune
  // it, and why this one is a money path.
  const selectedPayouts = selectedRows(selection, eligiblePayouts ?? [], (p) => p.id)
  const selectedIds = selectedPayouts.map((p) => p.id)
  const totalAmount = selectedPayouts.reduce((sum, p) => sum + p.requested_amount, 0)
  // The same intersection in the shape the table's checkboxes read, so the control agrees
  // with every number derived above rather than with the ids it was last handed.
  const loadedSelection: SelectionModel = {
    mode: 'loaded',
    selectedIds: Object.fromEntries(selectedIds.map((id) => [id, true])),
  }
  // `allMatching` is handed to the table AS IS. Pruning it into `loaded` is what made the header
  // read fully-checked over a partial selection: the pruned model carries an id for every eligible
  // payout, while the table's own loaded count is just the visible page, so `selectedCount >=
  // loadedCount` held even after a row was unticked. In `allMatching` the DS derives the header
  // from `deselectedIds` instead, which is exact however the rows are paginated — and there is
  // nothing to prune, because the model names EXCLUSIONS rather than members, so a refetch that
  // drops rows cannot strand a stale id in it.
  const tableSelection: SelectionModel =
    selection.mode === 'allMatching' ? selection : loadedSelection

  const handleProviderNext = () => {
    if (!provider) {
      return
    }
    setSelection(EMPTY_SELECTION)
    setStep('select-payouts')
  }

  const handlePayoutsNext = () => {
    if (selectedIds.length === 0) {
      return
    }
    setStep('confirm')
  }

  const handleBack = () => {
    createError.clearError()
    if (step === 'confirm') {
      setStep('select-payouts')
    } else if (step === 'select-payouts') {
      setStep('select-provider')
    } else {
      void navigate({ to: '/admin/settlements' })
    }
  }

  const handleCreate = () => {
    createMutation.mutate(
      {
        provider,
        currency,
        payout_ids: selectedIds,
      },
      {
        onSuccess: (data) => {
          void navigate({
            to: '/admin/settlements/$settlementId',
            params: { settlementId: data.id },
          })
        },
        onError: createError.onError,
      },
    )
  }

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="admin.settlements.createTitle" />}
        actions={
          <Button
            variant="text"
            startIcon={
              <ArrowLeftIcon
                height={16}
                width={16}
              />
            }
            onClick={handleBack}
          >
            <FormattedMessage id="admin.settlements.back" />
          </Button>
        }
        subtitle={formatMessage(
          { id: 'admin.settlements.createStepProgress' },
          {
            step: STEP_NUMBER[step],
            label: formatMessage({ id: STEP_LABELS[step] }),
          },
        )}
      />

      <Page.Content>
        {/* Step 1: Select Provider */}
        {step === 'select-provider' && (
          <StatsCard
            className="max-w-[500px] flex-none"
            icon={<CreditCardIcon />}
            title={<FormattedMessage id="admin.settlements.createStep1" />}
          >
            {overviewError ? (
              <InlineQueryError
                data-tst="create-overview-error"
                messageId="admin.settlements.error"
                onRetry={() => void refetchOverview()}
              />
            ) : (
              <div className="flex flex-col gap-5">
                {/* A provider with no eligible payouts stays visible but greyed
                    (MUI parity) — hiding it would read as "provider missing". */}
                <SelectField
                  label={formatMessage({ id: 'admin.settlements.provider' })}
                  value={provider}
                  options={CREATABLE_PROVIDERS.map((value) => {
                    const count = providerEligibleCount(overview, value)
                    const label = PROVIDER_LABELS[value] ?? value

                    return {
                      value,
                      disabled: count === 0,
                      // MUI parity: a disabled (zero-count) provider shows the
                      // plain label, no count suffix.
                      label:
                        count === 0 ? (
                          label
                        ) : (
                          <>
                            {label}
                            <Typography
                              className="ml-2"
                              color="text.secondary"
                              variant="caption"
                            >
                              {formatMessage(
                                { id: 'admin.settlements.providerPayoutCount' },
                                { count },
                              )}
                            </Typography>
                          </>
                        ),
                    }
                  })}
                  onValueChange={setProvider}
                />
                <SelectField
                  label={formatMessage({ id: 'admin.settlements.currency' })}
                  options={[{ value: 'USD', label: 'USD' }]}
                  value={currency}
                  onValueChange={setCurrency}
                />
                <div className="flex justify-end">
                  <Button
                    disabled={!provider}
                    variant="contained"
                    onClick={handleProviderNext}
                  >
                    <FormattedMessage id="admin.settlements.next" />
                  </Button>
                </div>
              </div>
            )}
          </StatsCard>
        )}

        {/* Step 2: Select Payouts */}
        {step === 'select-payouts' && (
          <div className="flex flex-col gap-4">
            {payoutsError ? (
              <InlineQueryError
                data-tst="create-payouts-error"
                messageId="admin.settlements.error"
                onRetry={() => void refetchPayouts()}
              />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Typography
                    color="text.secondary"
                    variant="body2"
                  >
                    {formatMessage(
                      { id: 'admin.settlements.selectedCount' },
                      { count: selectedIds.length, total: eligiblePayouts?.length ?? 0 },
                    )}
                  </Typography>
                </div>

                <DataTable
                  columns={payoutColumns}
                  data={eligiblePayouts ?? []}
                  // 25 was DataGridPro's `initialState` page size here, and the pager's
                  // select renders no option for a size it wasn't offered — so the default
                  // has to be one of these, or the control shows a blank value.
                  defaultPageSize={25}
                  getRowId={(row) => row.id}
                  loading={payoutsLoading}
                  pageSizeOptions={[25, 50, 100]}
                  selection={{
                    // The PRUNED model, not the raw one: an eligible-payouts refetch that
                    // drops a selected row would otherwise leave the header checkbox
                    // indeterminate over a table where nothing is ticked, disagreeing with
                    // the "N selected" count beside it (which reads the intersection).
                    model: tableSelection,
                    // The whole eligible set, NOT the visible page. The DS default scopes
                    // header select-all to `table.getRowModel().rows`, which under client
                    // pagination is the current 25 — so past one page an admin ticking the
                    // header saw every visible box checked while `payout_ids` carried only
                    // that page, silently under-paying the provider. `allMatching` is exact
                    // here because the eligible set is bounded and fully loaded: the
                    // exclusion model resolves through `selectedRows` to every eligible
                    // payout, and unticking a row still removes just that one.
                    selectAllScope: 'allMatching',
                    onSelectionChange: setSelection,
                    selectAllLabel: formatMessage({ id: 'common.table.selectAll' }),
                    selectRowLabel: formatMessage({ id: 'common.table.selectRow' }),
                  }}
                />

                <div className="flex items-center justify-between">
                  {selectedIds.length > 0 ? (
                    <div className="flex items-baseline gap-4">
                      <Typography
                        color="text.secondary"
                        variant="body2"
                      >
                        <FormattedMessage id="admin.settlements.selectedTotal" />:
                      </Typography>
                      <Typography
                        color="primary"
                        variant="h6"
                      >
                        <FormattedNumber
                          currency={currency}
                          style="currency"
                          value={totalAmount}
                        />
                      </Typography>
                    </div>
                  ) : (
                    <div />
                  )}
                  <Button
                    disabled={selectedIds.length === 0}
                    variant="contained"
                    onClick={handlePayoutsNext}
                  >
                    <FormattedMessage id="admin.settlements.review" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && (
          <div className="flex max-w-[700px] flex-col gap-6">
            <StatsCard
              className="flex-none"
              icon={<CreditCardIcon />}
              title={<FormattedMessage id="admin.settlements.createStep3" />}
            >
              <div className="flex flex-col gap-2">
                <Row
                  label={<FormattedMessage id="admin.settlements.provider" />}
                  value={PROVIDER_LABELS[provider] ?? provider}
                />
                <Row
                  label={<FormattedMessage id="admin.settlements.currency" />}
                  value={currency}
                />
                <Row
                  label={<FormattedMessage id="admin.settlements.payoutCount" />}
                  value={<FormattedNumber value={selectedIds.length} />}
                />
                <Row
                  label={<FormattedMessage id="admin.settlements.totalAmount" />}
                  value={
                    <Typography color="primary">
                      <FormattedNumber
                        currency={currency}
                        style="currency"
                        value={totalAmount}
                      />
                    </Typography>
                  }
                />
              </div>
            </StatsCard>

            <StatsCard
              className="flex-none"
              icon={<BanknotesIcon />}
              title={<FormattedMessage id="admin.settlements.includedPayouts" />}
            >
              <div className="flex max-h-[300px] flex-col gap-1.5 overflow-auto">
                {selectedPayouts.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex flex-col">
                      <Typography variant="body2">{p.partner_display_name}</Typography>
                      <Typography
                        color="text.secondary"
                        variant="caption"
                      >
                        {p.partner_email}
                      </Typography>
                    </div>
                    <Typography variant="body2">
                      <FormattedNumber
                        currency={currency}
                        style="currency"
                        value={p.requested_amount}
                      />
                    </Typography>
                  </div>
                ))}
              </div>
            </StatsCard>

            <Alert
              message={<FormattedMessage id="admin.settlements.confirmNote" />}
              severity="info"
            />

            <MutationError
              data-tst="create-settlement-error"
              messageId="admin.settlements.createError"
              show={createError.hasError}
            />

            <div className="flex justify-end gap-2">
              <Button
                disabled={createMutation.isPending}
                onClick={handleBack}
              >
                <FormattedMessage id="admin.settlements.back" />
              </Button>
              <Button
                color="primary"
                // `selectedIds` is the intersection with the payouts still eligible, so it can
                // empty out from under a confirm step left open (a focus refetch, or another
                // admin settling them). `handlePayoutsNext` only guards the way IN to this
                // step; without this the button would POST an empty `payout_ids`.
                disabled={createMutation.isPending || selectedIds.length === 0}
                variant="contained"
                onClick={handleCreate}
              >
                <FormattedMessage id="admin.settlements.createSettlement" />
              </Button>
            </div>
          </div>
        )}
      </Page.Content>
    </Page>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

type RowProps = {
  label: React.ReactNode
  value: React.ReactNode
}

const Row = ({ label, value }: RowProps) => (
  <div className="flex items-baseline justify-between">
    <Typography
      color="text.secondary"
      variant="body2"
    >
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </div>
)
